import { useState, useEffect, useCallback } from 'react';
import { getDayTabLabels } from '@/mocks/generationForecast';

/**
 * Обрана доба сторінки «Прогноз/MMS».
 *
 * Живе тут, а не в самій сторінці, бо її поділяють ДВА місця: тіло сторінки і
 * винесене в топбар керування добою (◀ дата ▶ + вкладки D-2…D+2). Той самий
 * прийом, що й useSelectedAsset — модульне значення + подія синхронізації в
 * межах вкладки; браузерна подія 'storage' спрацьовує лише для ІНШИХ вкладок,
 * тож для миттєвого оновлення сусіднього компонента потрібна власна.
 *
 * Без localStorage: доба скидається на «сьогодні» при перезавантаженні, що і є
 * розумним типовим значенням. Зберігати її між сесіями сенсу немає.
 */
const CHANGE_EVENT = 'helios:fcmms-date-change';

export interface DayTab { label: string; apiDate: string }

/** Вкладки D-2 · D-1 · Сьогодні · D+1 · D+2 — те саме джерело, що й раніше. */
export function getFcmmsDayTabs(): DayTab[] {
  return getDayTabLabels() as DayTab[];
}

/** apiDate вкладки «Today» — типова доба сторінки. */
export function fcmmsToday(): string {
  const tabs = getFcmmsDayTabs();
  return tabs.find((d) => d.label === 'Today')?.apiDate ?? tabs[2]?.apiDate ?? '';
}

/** Зсув ISO-дати на ±N діб у UTC (без стрибків через літній/зимовий час). */
export function shiftDay(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

let current = fcmmsToday();

export function useForecastMmsDate() {
  const [date, setDateState] = useState(current);

  const setDate = useCallback((d: string) => {
    current = d;
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
    setDateState(d);
  }, []);

  useEffect(() => {
    const refresh = () => setDateState(current);
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => window.removeEventListener(CHANGE_EVENT, refresh);
  }, []);

  return { date, setDate };
}
